/**
 * Logo görselinden baskın (vurgu) rengi çıkarır ve buna dayalı site paletini üretir.
 * Site rengi = marka ana rengi + hover tonu + footer arka plan / yazı renkleri.
 */

export interface SitePalette {
  primary: string;
  hover: string;
  footerBackground: string;
  footerText: string;
  /** Solid koyu/açık metin (başlıklar) */
  footerHeading: string;
  /** Yumuşatılmış metin (açıklama, iletişim, linkler) */
  footerMuted: string;
  footerLink: string;
  /** Kurumsal hero gradient arka plan */
  heroGradient: string;
  /** Açık/solid hero arka plan (minimal şablonlar, şube vb.) */
  heroSolid: string;
  heroOverlay: string;
  heroTitleOnDark: string;
  heroDescriptionOnDark: string;
  heroTitleOnLight: string;
  heroDescriptionOnLight: string;
}

interface Rgb {
  r: number;
  g: number;
  b: number;
}

const clamp = (v: number, min = 0, max = 255): number => Math.max(min, Math.min(max, v));

const toHex = (v: number): string => clamp(Math.round(v)).toString(16).padStart(2, '0');

export const rgbToHex = ({ r, g, b }: Rgb): string => `#${toHex(r)}${toHex(g)}${toHex(b)}`;

export const hexToRgb = (hex: string): Rgb | null => {
  const v = (hex || '').trim().replace('#', '');
  if (v.length === 3) {
    const r = parseInt(v[0] + v[0], 16);
    const g = parseInt(v[1] + v[1], 16);
    const b = parseInt(v[2] + v[2], 16);
    return { r, g, b };
  }
  if (v.length === 6) {
    const r = parseInt(v.slice(0, 2), 16);
    const g = parseInt(v.slice(2, 4), 16);
    const b = parseInt(v.slice(4, 6), 16);
    if ([r, g, b].some(Number.isNaN)) return null;
    return { r, g, b };
  }
  return null;
};

/** 0..1 arası göreli parlaklık (WCAG). */
const relativeLuminance = ({ r, g, b }: Rgb): number => {
  const srgb = [r, g, b].map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * srgb[0] + 0.7152 * srgb[1] + 0.0722 * srgb[2];
};

/** Renk üzerine gelecek metin için beyaz mı koyu mu kontrast rengini döndürür. */
export const getContrastText = (hex: string): string => {
  const rgb = hexToRgb(hex);
  if (!rgb) return '#ffffff';
  return relativeLuminance(rgb) > 0.45 ? '#111827' : '#ffffff';
};

/** Rengi siyaha doğru karartır (amount: 0..1). */
export const darken = (hex: string, amount: number): string => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const f = 1 - clamp(amount, 0, 1);
  return rgbToHex({ r: rgb.r * f, g: rgb.g * f, b: rgb.b * f });
};

/** Rengi beyaza doğru açar (amount: 0..1). */
export const lighten = (hex: string, amount: number): string => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const a = clamp(amount, 0, 1);
  return rgbToHex({
    r: rgb.r + (255 - rgb.r) * a,
    g: rgb.g + (255 - rgb.g) * a,
    b: rgb.b + (255 - rgb.b) * a,
  });
};

const saturationOf = ({ r, g, b }: Rgb): number => {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  if (max === 0) return 0;
  return (max - min) / max;
};

/**
 * Bir rengi footer için yeterince koyu bir tona çeker.
 * Renk zaten koyuysa hafif, açıksa güçlü karartma uygular; hue korunur.
 */
const toFooterBackground = (hex: string): string => {
  const rgb = hexToRgb(hex);
  if (!rgb) return '#1f2937';
  const lum = relativeLuminance(rgb);
  // Hedef: koyu ama markanın tonunu taşıyan bir arka plan.
  if (lum > 0.5) return darken(hex, 0.72);
  if (lum > 0.25) return darken(hex, 0.55);
  if (lum > 0.1) return darken(hex, 0.3);
  return hex;
};

/** Ana renkten tüm site paletini türetir. */
export const deriveSitePalette = (baseHex: string): SitePalette => {
  const primary = baseHex;
  const hover = darken(baseHex, 0.14);
  const footerBackground = toFooterBackground(baseHex);
  const footerText = getContrastText(footerBackground);
  const isDarkFooter = footerText === '#ffffff';
  const footerHeading = footerText;
  const footerMuted = isDarkFooter ? 'rgba(255, 255, 255, 0.78)' : 'rgba(17, 24, 39, 0.70)';
  const footerLink = isDarkFooter ? 'rgba(255, 255, 255, 0.82)' : 'rgba(17, 24, 39, 0.72)';
  const heroGradient = `linear-gradient(135deg, ${primary} 0%, ${hover} 100%)`;
  const heroSolid = lighten(primary, 0.9);
  const heroOverlay = darken(primary, 0.35);
  return {
    primary,
    hover,
    footerBackground,
    footerText,
    footerHeading,
    footerMuted,
    footerLink,
    heroGradient,
    heroSolid,
    heroOverlay,
    heroTitleOnDark: '#ffffff',
    heroDescriptionOnDark: 'rgba(255, 255, 255, 0.88)',
    heroTitleOnLight: '#111827',
    heroDescriptionOnLight: 'rgba(17, 24, 39, 0.72)',
  };
};

/** Mevcut hero arka planına göre (gradient mi solid mı) siteye uyumlu yeni renk. */
export const resolveHeroBackground = (currentBg: string | null | undefined, palette: SitePalette): string => {
  const bg = (currentBg || '').trim();
  if (!bg || bg.includes('gradient')) return palette.heroGradient;
  return palette.heroSolid;
};

/** Hero settingsJson design alanını site paletine göre günceller. */
export const patchHeroSettingsJson = (
  raw: string | object | null | undefined,
  palette: SitePalette,
  nextBackground: string
): string => {
  let parsed: Record<string, any> = {};
  if (typeof raw === 'string' && raw.trim()) {
    try {
      parsed = JSON.parse(raw) || {};
    } catch {
      parsed = {};
    }
  } else if (raw && typeof raw === 'object') {
    parsed = { ...(raw as Record<string, any>) };
  }

  const isDark = nextBackground.includes('gradient') || getContrastText(nextBackground) === '#ffffff';
  parsed.design = {
    ...(parsed.design || {}),
    overlayColor: palette.heroOverlay,
    titleColor: isDark ? palette.heroTitleOnDark : palette.heroTitleOnLight,
    descriptionColor: isDark ? palette.heroDescriptionOnDark : palette.heroDescriptionOnLight,
    badgeColor: palette.primary,
    badgeTextColor: getContrastText(palette.primary),
  };

  return JSON.stringify(parsed);
};

const colorDistance = (a: Rgb, b: Rgb): number =>
  Math.sqrt((a.r - b.r) ** 2 + (a.g - b.g) ** 2 + (a.b - b.b) ** 2);

/**
 * Logo görselinden baskın renk adaylarını (en fazla `maxColors`) çıkarır.
 * - Saydam / yakın-beyaz / yakın-siyah pikseller elenir.
 * - Skor prominence (alan) + doygunluk dengesiyle hesaplanır; böylece küçük
 *   ama çok doygun bir renk (ör. kırmızı) tek başına baskın seçilmez.
 * - Sonuçlar renk uzaklığına göre çeşitlendirilir (kırmızı, gri, koyu vb. ayrı çıkar).
 * CORS izni olmayan görsellerde reddeder (getImageData taint eder).
 */
export const extractLogoPalette = (imageUrl: string, maxColors = 5): Promise<string[]> =>
  new Promise((resolve, reject) => {
    if (!imageUrl) {
      reject(new Error('no-image'));
      return;
    }

    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      try {
        const size = 80;
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d', { willReadFrequently: true } as any);
        if (!ctx) {
          reject(new Error('no-context'));
          return;
        }
        ctx.drawImage(img, 0, 0, size, size);

        const { data } = ctx.getImageData(0, 0, size, size);

        const buckets = new Map<string, { count: number; score: number; r: number; g: number; b: number }>();

        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          const a = data[i + 3];

          if (a < 125) continue; // saydam
          if (r > 240 && g > 240 && b > 240) continue; // yakın-beyaz
          if (r < 12 && g < 12 && b < 12) continue; // yakın-siyah

          const sat = saturationOf({ r, g, b });
          // Prominence ağırlıklı: her piksel taban 1 + doygunluk katkısı.
          // Böylece geniş gri alan da, küçük canlı alan da adil temsil edilir.
          const weight = 1 + sat * 1.4;

          const key = `${r >> 4}-${g >> 4}-${b >> 4}`; // 4 bit kuantalama (kaba gruplama)
          const existing = buckets.get(key);
          if (existing) {
            existing.count += 1;
            existing.score += weight;
            existing.r += r;
            existing.g += g;
            existing.b += b;
          } else {
            buckets.set(key, { count: 1, score: weight, r, g, b });
          }
        }

        if (buckets.size === 0) {
          reject(new Error('no-color'));
          return;
        }

        const ranked = Array.from(buckets.values())
          .map((bucket) => ({
            rgb: {
              r: bucket.r / bucket.count,
              g: bucket.g / bucket.count,
              b: bucket.b / bucket.count,
            } as Rgb,
            score: bucket.score,
          }))
          .sort((x, y) => y.score - x.score);

        // Çeşitlilik: birbirine çok yakın renkleri ele.
        const chosen: Rgb[] = [];
        for (const item of ranked) {
          if (chosen.length >= maxColors) break;
          const isDistinct = chosen.every((c) => colorDistance(c, item.rgb) > 55);
          if (isDistinct) chosen.push(item.rgb);
        }

        if (chosen.length === 0) {
          reject(new Error('no-color'));
          return;
        }

        resolve(chosen.map(rgbToHex));
      } catch (err) {
        // Genellikle CORS taint (SecurityError)
        reject(err instanceof Error ? err : new Error('extract-failed'));
      }
    };

    img.onerror = () => reject(new Error('image-load-failed'));
    img.src = imageUrl;
  });

/** Geriye dönük: tek baskın renk (palet ilk elemanı). */
export const extractDominantColor = async (imageUrl: string): Promise<string> => {
  const palette = await extractLogoPalette(imageUrl, 1);
  return palette[0];
};
